import React, { ReactNode, useState } from "react";

import { Stack } from "@mui/material";
import { colors } from "@zapalacorp/tokens";
import { useTranslation } from "react-i18next";

import { Button } from "../../../../../../../../../../components/Form";
import BadgeJobStatus from "../../../../../../../../../../components/Generic/BadgeJobStatus";
import BadgeType from "../../../../../../../../../../components/Generic/BadgeType";
import StyledBadge from "../../../../../../../../../../components/Generic/StyledBadge";
import { Order } from "../../../../../../../../../../models/order";
import { calculateTotalVolume } from "../../../../../../../../../../util/calculateTotalVolume";
import { ItemJobSubStatus } from "../../../../../../../../../../util/enums";
import { formatStorageBoxesFromOrder } from "../../../../../../../../../../util/formatStorageBoxesFromOrder";
import { getMeasurement } from "../../../../../../../../../../util/getMeasurement";
import { getOrderCustomerDisplayName } from "../../../../../../../../../../util/names";
import DrawerViewOrder from "./DrawerViewOrder";
import SkeletonCard from "./Skeleton";
import {
  Container,
  Content,
  InfoText,
  Line,
  StackRowContent,
  StackTextEnd,
  SubTitleInfo,
  TextVolume,
  Title,
  Required,
  TitleContainer,
} from "./styles";

type Props = {
  title?: ReactNode;
  info?: string;
  tooltip?: ReactNode;
  volume?: ReactNode;
  orders?: Order[];
  isViewMode?: boolean;
  isLoading?: boolean;
  paginate?: ReactNode;
  required?: boolean;
  hasError?: boolean;
  renderButton?: (order: Order, index: number) => ReactNode;
  hideView?: (order: Order) => boolean;
  onSaveOrder?: (order: Order) => void;
};

const CardContainer = (props: Props) => {
  const {
    title,
    info,
    tooltip,
    volume,
    renderButton,
    orders,
    onSaveOrder,
    hideView,
    isViewMode = false,
    paginate,
    isLoading,
    required = false,
    hasError = false,
  } = props;

  const measurement = getMeasurement();

  const { t: translate } = useTranslation();

  const [orderToView, setOrderToView] = useState<Order>();

  const genericBadge = (text?: string) => {
    return <StyledBadge backgroundColor={colors.gray50} textColor={colors.gray600} value={text} />;
  };

  const renderOrders = () => {
    if (orders && orders?.length) {
      return orders?.map((order, index) => {
        const badgeItems =
          order?.itemsJob && order?.itemsJob?.length > 1
            ? `${order?.itemsJob?.length || 0} ${translate("items")}`
            : `${order?.itemsJob?.length || 0} ${translate("item")}`;

        const containersList = formatStorageBoxesFromOrder(order?.itemsJob || []);

        return (
          <Content key={order?.id || index}>
            <StackRowContent justifyContent="space-between" alignItems="center" direction="row">
              <Stack spacing="8px" direction="row">
                <BadgeType value={order?.type} />
                <BadgeJobStatus value={order?.status} />
              </Stack>
              <StackTextEnd spacing="8px" direction="row" justifyContent="flex-end">
                {order?.mode ? genericBadge(translate(order.mode.toLowerCase())) : null}
                {genericBadge(
                  calculateTotalVolume(ItemJobSubStatus.ACTUAL, measurement, order?.itemsJob)
                )}
                {genericBadge(badgeItems)}
              </StackTextEnd>
            </StackRowContent>
            <StackRowContent justifyContent="space-between" direction="row">
              <Stack direction="column">
                <Title color={colors.brandBlue500}>{`${order.reference}${
                  order.sequence ? `/${order.sequence}` : ""
                }`}</Title>
                <SubTitleInfo fontWeight={600}>{getOrderCustomerDisplayName(order)}</SubTitleInfo>
              </Stack>
              {containersList ? (
                <StackTextEnd direction="column" justifyContent="flex-end">
                  <SubTitleInfo fontWeight={400}>{translate("containers")}</SubTitleInfo>
                  <SubTitleInfo fontWeight={600}>{containersList}</SubTitleInfo>
                </StackTextEnd>
              ) : null}
            </StackRowContent>
            <Line />
            <StackRowContent justifyContent="flex-end" direction="row">
              {hideView && hideView(order) ? null : (
                <Button onClick={() => setOrderToView(order)}>{translate("view")}</Button>
              )}
              {renderButton && renderButton(order, index)}
            </StackRowContent>
          </Content>
        );
      });
    }

    return null;
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Stack width="100%" gap="24px">
          <SkeletonCard />
          {paginate}
        </Stack>
      );
    }

    return (
      <>
        <Stack
          width="100%"
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          height={24}
        >
          <TitleContainer>
            <Title color={colors.gray600}>
              {title}
              {required ? <Required>*</Required> : null}
            </Title>
            {tooltip}
          </TitleContainer>
          <Stack direction="row" alignItems="center" spacing={1}>
            <InfoText>{info}</InfoText>
            <TextVolume>{volume}</TextVolume>
          </Stack>
        </Stack>
        <Stack width="100%" gap="24px">
          {renderOrders()}
          {paginate}
        </Stack>
      </>
    );
  };

  return (
    <Container hasError={hasError}>
      <DrawerViewOrder
        order={orderToView}
        onSave={(order) => {
          setOrderToView(undefined);
          if (onSaveOrder && order) onSaveOrder(order);
        }}
        onCancel={() => setOrderToView(undefined)}
        isViewMode={isViewMode}
      />
      {renderContent()}
    </Container>
  );
};

export default CardContainer;
